import { useCallback, useEffect, useState } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { fetchAuthenticatedFileBlob } from '../../services/api';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string | null;
  /** Chemin API relatif, ex. `/admin/documents/:id/fichier` */
  apiPath: string | null;
};

function isImageType(contentType: string) {
  return contentType.startsWith('image/');
}

function isPdfType(contentType: string) {
  return contentType === 'application/pdf' || contentType.includes('pdf');
}

export default function DocumentPreviewModal({ open, onClose, title, subtitle, apiPath }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');

  const cleanup = useCallback(() => {
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setContentType('');
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    cleanup();
    onClose();
  }, [cleanup, onClose]);

  useEffect(() => {
    if (!open) {
      cleanup();
      return undefined;
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, handleClose, cleanup]);

  useEffect(() => {
    if (!open || !apiPath) {
      return undefined;
    }

    let cancelled = false;
    let createdUrl: string | null = null;

    (async () => {
      setLoading(true);
      setError(null);
      cleanup();
      try {
        const result = await fetchAuthenticatedFileBlob(apiPath);
        if (cancelled) {
          URL.revokeObjectURL(result.blobUrl);
          return;
        }
        createdUrl = result.blobUrl;
        setBlobUrl(result.blobUrl);
        setContentType(result.contentType);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Impossible de charger le document.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, apiPath, cleanup]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) handleClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-preview-title"
        className="flex max-h-[min(90vh,900px)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-outline-variant/15 px-5 py-4">
          <div className="min-w-0">
            <h2 id="document-preview-title" className="truncate text-lg font-black text-slate-900">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 text-sm text-on-surface-variant">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-outline-variant/25 bg-white text-on-surface-variant transition hover:bg-surface-container-low hover:text-slate-900"
            aria-label="Fermer"
          >
            <LucideSymbolIcon name="close" tone="inherit" size={22} />
          </button>
        </div>

        <div className="flex min-h-[280px] flex-1 flex-col items-center justify-center overflow-auto bg-slate-100/80 p-4 sm:min-h-[360px]">
          {loading ? (
            <p className="text-sm font-medium text-on-surface-variant">Chargement du document…</p>
          ) : error ? (
            <p className="max-w-md rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-red-800">{error}</p>
          ) : blobUrl && isImageType(contentType) ? (
            <img
              src={blobUrl}
              alt={title}
              className="max-h-[min(70vh,720px)] max-w-full rounded-lg border border-outline-variant/15 bg-white object-contain shadow-sm"
            />
          ) : blobUrl && isPdfType(contentType) ? (
            <iframe
              title={title}
              src={blobUrl}
              className="h-[min(70vh,720px)] w-full rounded-lg border border-outline-variant/15 bg-white shadow-sm"
            />
          ) : blobUrl ? (
            <div className="max-w-md rounded-xl bg-white p-6 text-center shadow-sm">
              <LucideSymbolIcon name="description" tone="muted" size={40} className="mx-auto" />
              <p className="mt-3 text-sm text-on-surface-variant">
                Aperçu non disponible pour ce type de fichier.
              </p>
              <a
                href={blobUrl}
                download
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white"
              >
                <LucideSymbolIcon name="download" tone="inherit" size={18} className="text-white" />
                Télécharger
              </a>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-outline-variant/15 px-5 py-3">
          {blobUrl ? (
            <a
              href={blobUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2 text-xs font-bold text-primary hover:bg-primary/10"
            >
              <LucideSymbolIcon name="public" tone="inherit" size={16} />
              Ouvrir dans un nouvel onglet
            </a>
          ) : null}
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:opacity-95"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
